interface DeadlineOptions {timeoutMs: number; message: string; parentSignal?: AbortSignal | null}

// Covers the complete operation, including JSON/stream consumption, not only headers.
export async function withDeadline<T>(operation: (signal: AbortSignal) => Promise<T>, options: DeadlineOptions): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  let rejectInterruption: (reason: unknown) => void = () => undefined;
  const interrupted = new Promise<never>((_, reject) => { rejectInterruption = reject; });
  const abortFromParent = (): void => {
    controller.abort();
    rejectInterruption(new DOMException('Request cancelled', 'AbortError'));
  };
  if (options.parentSignal?.aborted) abortFromParent();
  options.parentSignal?.addEventListener('abort', abortFromParent, {once: true});
  const timer = window.setTimeout(() => {
    timedOut = true; controller.abort(); rejectInterruption(new Error(options.message));
  }, options.timeoutMs);
  try {
    return await Promise.race([operation(controller.signal), interrupted]);
  } catch (error: unknown) {
    if (timedOut) throw new Error(options.message);
    throw error;
  } finally {
    window.clearTimeout(timer);
    options.parentSignal?.removeEventListener('abort', abortFromParent);
  }
}